import { useCallback, useEffect, useRef, useState } from 'react';
import proDocumentsService, { type ProMargin, type DriverPayLeg } from '../services/proDocuments.service';

export interface ProMarginState {
  /** Full breakdown is fetched only on explicit refresh/open. */
  margin: ProMargin | null;
  savedMarginPct: number | null;
  marginLimit: number;
  byLeg: Map<number, DriverPayLeg>;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

type MarginData = Omit<ProMarginState, 'refresh'>;
const EMPTY: MarginData = {
  margin: null, savedMarginPct: null, marginLimit: 0,
  byLeg: new Map(), loading: false, error: null,
};

/** Initial PRO load reads pay + saved margin. Explicit breakdown views may opt
 * into a full calculation on mount (e.g. Invoice List's user-opened modal). */
export function useProMargin(pronumber: number | null | undefined, fullOnMount = false): ProMarginState {
  const [state, setState] = useState(EMPTY);
  const [reloadNonce, setReloadNonce] = useState(0);
  const prevProRef = useRef<number | null>(null);
  const fullRequested = useRef(false);
  const refresh = useCallback(() => {
    fullRequested.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));
    setReloadNonce(n => n + 1);
  }, []);

  useEffect(() => {
    const pro = Number(pronumber);
    if (!Number.isFinite(pro) || pro <= 0) {
      setState(EMPTY);
      prevProRef.current = null;
      return;
    }
    const proChanged = prevProRef.current !== pro;
    if (proChanged) fullRequested.current = false;
    prevProRef.current = pro;
    const full = fullOnMount || fullRequested.current;
    let cancelled = false;
    setState(prev => ({ ...(proChanged ? EMPTY : prev), loading: true, error: null }));
    const load = async () => {
      try {
        if (full) {
          const margin = await proDocumentsService.getMargin(pro);
          if (cancelled) return;
          setState({ margin, savedMarginPct: margin.margin.total.marginPct,
            marginLimit: margin.margin.marginLimit,
            byLeg: new Map(margin.driverPay.legs.map(leg => [leg.legnum, leg])),
            loading: false, error: null });
        } else {
          const pay = (await proDocumentsService.getDriverPay([pro]))[pro];
          if (cancelled) return;
          setState({ margin: null, savedMarginPct: pay?.savedMarginPct ?? null,
            marginLimit: pay?.marginLimit ?? 0,
            byLeg: new Map((pay?.driverPay.legs ?? []).map(leg => [leg.legnum, leg])),
            loading: false, error: null });
        }
      } catch {
        if (!cancelled) setState(prev => ({ ...prev, margin: null, loading: false,
          error: full ? 'Could not calculate and save margin. Click Margin to retry.' : 'Could not load driver pay.' }));
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [pronumber, reloadNonce, fullOnMount]);

  return { ...state, refresh };
}
